import React, { useState, useEffect, useMemo } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Dropdown } from "../../../../components/Dropdown";
import { Button } from "../../../../components/Button";
import { apiGet, apiPost, apiPut } from "../../../../services/apiServices";
import { endPoints } from "../../../../configs/apiUrls";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import indiaData from "../../../../data/stateData.json";

const AddAllProperty = () => {
    const { id } = useParams<{ id?: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const isEditMode = Boolean(id);

    const [initialValues, setInitialValues] = useState({
        state: "Karnataka",
        city: "",
        area: "",
        street: "",
        wardNo: "",
        blockNo: "",
        surveyNumbers: "",
        guidelineRate: "",
        buaGuidelineRatePerSqft: "", // Added BUA Guideline Rate
        marketRateMin: "",
        marketRateMax: "",
    });

    const [areaOptions, setAreaOptions] = useState<any[]>([]);
    const [streetOptions, setStreetOptions] = useState<any[]>([]);

    const stateOptions = useMemo(
        () => indiaData.map((s) => ({ label: s.state, value: s.state })),
        []
    );

    const cityOptions = useMemo(
        () =>
            indiaData.flatMap((s) =>
                s.districts.map((d) => ({
                    label: d,
                    value: d,
                    state: s.state,
                }))
            ),
        []
    );

    const getAreaList = async (city: string) => {
        try {
            const res: any = await apiGet(`${endPoints.GET_CITY_BY_STATE}/${city}`);
            setAreaOptions(res?.data || []);
        } catch (error) {
            console.error(error);
            setAreaOptions([]);
        }
    };

    const getStreetList = async (areaId: string) => {
        try {
            const res: any = await apiGet(`${endPoints.GET_STREET_BY_AREA}/${areaId}`);
            setStreetOptions(res?.data || []);
        } catch {
            setStreetOptions([]);
        }
    };

    // Load edit data
    useEffect(() => {
        if (isEditMode && location.state?.row) {
            const row = location.state.row;

            getAreaList(row.city);
            getStreetList(row.area?._id);

            setInitialValues({
                state: row.state,
                city: row.city,
                area: row.area?._id,
                street: row.street?._id,
                wardNo: row.wardNo || "",
                blockNo: row.blockNo || "",
                surveyNumbers: (row.surveyNumbers || []).join(", "),
                guidelineRate: row.guidelineRate,
                buaGuidelineRatePerSqft: row.buaGuidelineRatePerSqft || "", // Added to edit mode
                marketRateMin: row.marketRate?.min,
                marketRateMax: row.marketRate?.max,
            });
        }
    }, [isEditMode, location.state]);

    // VALIDATION
    const validationSchema = Yup.object().shape({
        state: Yup.string().required("State is required"),
        city: Yup.string().required("City is required"),
        area: Yup.string().required("Area is required"),
        street: Yup.string().required("Street is required"),
        guidelineRate: Yup.number().positive("Invalid").required("Required"),
        buaGuidelineRatePerSqft: Yup.number().positive("Invalid").required("Required"), // Added validation
        marketRateMin: Yup.number().positive("Invalid").required("Required"),
        marketRateMax: Yup.number().positive("Invalid").required("Required"),
    });

    const handleSubmit = async (values: typeof initialValues, { setSubmitting }: any) => {
        setSubmitting(true);

        const payload = {
            state: values.state,
            city: values.city,
            area: values.area,
            street: values.street,
            wardNo: values.wardNo || null,
            blockNo: values.blockNo || null,
            surveyNumbers: values.surveyNumbers.split(",").map((s) => s.trim()),
            guidelineRate: Number(values.guidelineRate),
            buaGuidelineRatePerSqft: Number(values.buaGuidelineRatePerSqft), // Added to payload
            marketRate: {
                min: Number(values.marketRateMin),
                max: Number(values.marketRateMax),
            },
        };

        try {
            const res = isEditMode
                ? await apiPut(`${endPoints.GET_ALL_PROPERTIES}/${id}`, payload)
                : await apiPost(endPoints.GET_ALL_PROPERTIES, payload);

            if (res?.success) {
                toast.success(`Property ${isEditMode ? "updated" : "created"} successfully!`);
                if (isEditMode) {
                    navigate(-1);
                } else {
                    setInitialValues({
                        ...initialValues,
                        surveyNumbers: "",
                        guidelineRate: "",
                        buaGuidelineRatePerSqft: "", // Added BUA Guideline Rate
                        marketRateMin: "",
                        marketRateMax: "",
                    })
                }
            } else {
                toast.error(
                    res?.data?.error?.existingSurveyNumbers?.length
                        ? `${res?.data?.message} ${JSON?.stringify(res?.data?.error?.existingSurveyNumbers)}`
                        : res?.data?.message
                            ? res?.data?.message
                            : "Operation failed"
                );
            }
        } catch (error) {
            toast.error("Something went wrong");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-9xl mx-auto p-6 bg-cardBackground rounded shadow">
            <h2 className="text-2xl font-semibold mb-6 text-headingText">
                {isEditMode ? "Edit Property Info" : "Add Property Info"}
            </h2>

            <Formik
                initialValues={initialValues}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, isSubmitting }) => {
                    const filteredCityOptions = cityOptions.filter((c) => c.state === values.state);

                    return (
                        <Form className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {/* STATE */}
                            <div>
                                <label className="block mb-1 font-medium">State *</label>
                                <Dropdown
                                    options={stateOptions}
                                    value={values.state}
                                    onChange={(val) => {
                                        setFieldValue("state", val);
                                        setFieldValue("city", "");
                                        setFieldValue("area", "");
                                        setAreaOptions([]);
                                    }}
                                    className="w-full"
                                />
                                <ErrorMessage name="state" component="div" className="text-errorText text-sm" />
                            </div>

                            {/* CITY */}
                            <div>
                                <label className="block mb-1 font-medium">City *</label>
                                <Dropdown
                                    options={filteredCityOptions}
                                    value={values.city}
                                    onChange={(val) => {
                                        setFieldValue("city", val);
                                        setFieldValue("area", "");
                                        getAreaList(val);
                                    }}
                                    disabled={!values.state}
                                    className="w-full"
                                />
                                <ErrorMessage name="city" component="div" className="text-errorText text-sm" />
                            </div>

                            {/* AREA */}
                            <div>
                                <label className="block mb-1 font-medium">Area *</label>
                                <Dropdown
                                    options={areaOptions}
                                    value={values.area}
                                    onChange={(val) => {
                                        setFieldValue("area", val);
                                        setFieldValue("street", "");
                                        getStreetList(val);
                                    }}
                                    disabled={!values.city}
                                    className="w-full"
                                />
                                <ErrorMessage name="area" component="div" className="text-errorText text-sm" />
                            </div>

                            {/* STREET */}
                            <div>
                                <label className="block mb-1 font-medium">Street *</label>
                                <Dropdown
                                    options={streetOptions}
                                    value={values.street}
                                    onChange={(val) => setFieldValue("street", val)}
                                    disabled={!values.area}
                                    className="w-full"
                                />
                                <ErrorMessage name="street" component="div" className="text-errorText text-sm" />
                            </div>

                            {/* WARD NO */}
                            <div>
                                <label className="block mb-1 font-medium">Ward No (optional)</label>
                                <Field type="text" name="wardNo" className="w-full border rounded px-3 py-2" />
                            </div>

                            {/* BLOCK NO */}
                            <div>
                                <label className="block mb-1 font-medium">Block No (optional)</label>
                                <Field type="text" name="blockNo" className="w-full border rounded px-3 py-2" />
                            </div>

                            {/* SURVEY NUMBERS */}
                            <div className="md:col-span-3">
                                <label className="block mb-1 font-medium">Survey Numbers</label>
                                <Field
                                    type="text"
                                    name="surveyNumbers"
                                    placeholder="Ex: 71/1, 72/3, 73/2A"
                                    className="w-full border rounded px-3 py-2"
                                />
                                {/* <ErrorMessage name="surveyNumbers" component="div" className="text-errorText text-sm" /> */}
                            </div>

                            {/* GUIDELINE RATE */}
                            <div>
                                <label className="block mb-1 font-medium">Guideline Rate *</label>
                                <Field type="number" name="guidelineRate" className="w-full border rounded px-3 py-2" />
                                <ErrorMessage name="guidelineRate" component="div" className="text-errorText text-sm" />
                            </div>

                            {/* BUA GUIDELINE RATE */}
                            <div>
                                <label className="block mb-1 font-medium">BUA Guideline Rate (per sqft) *</label>
                                <Field
                                    type="number"
                                    name="buaGuidelineRatePerSqft"
                                    className="w-full border rounded px-3 py-2"
                                />
                                <ErrorMessage
                                    name="buaGuidelineRatePerSqft"
                                    component="div"
                                    className="text-errorText text-sm"
                                />
                            </div>

                            {/* MARKET RATE MIN */}
                            <div>
                                <label className="block mb-1 font-medium">Market Rate Min *</label>
                                <Field type="number" name="marketRateMin" className="w-full border rounded px-3 py-2" />
                                <ErrorMessage name="marketRateMin" component="div" className="text-errorText text-sm" />
                            </div>

                            {/* MARKET RATE MAX */}
                            <div>
                                <label className="block mb-1 font-medium">Market Rate Max *</label>
                                <Field type="number" name="marketRateMax" className="w-full border rounded px-3 py-2" />
                                <ErrorMessage name="marketRateMax" component="div" className="text-errorText text-sm" />
                            </div>

                            {/* SUBMIT */}
                            <div className="md:col-span-3 pt-3">
                                <Button type="submit" loading={isSubmitting} variant="primary">
                                    {isEditMode ? "Update Property Info" : "Create Property Info"}
                                </Button>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default AddAllProperty;